import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'sonner'

export interface FileWithPreview extends File {
  preview?: string
  uploadProgress?: number
  status?: 'uploading' | 'done' | 'error'
  errorMessage?: string
  id?: number
}

interface DropzoneProps {
  onFilesAccepted: (files: FileWithPreview[]) => void
  maxSize?: number // in bytes
  maxFiles?: number
  acceptedFileTypes?: Record<string, string[]>
  allowFolders?: boolean
}

export const DropzoneComponent: React.FC<DropzoneProps> = ({
  onFilesAccepted,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 10,
  acceptedFileTypes,
  allowFolders = true,
}) => {
  const [isDragActive, setIsDragActive] = useState(false)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Process files including folders if allowFolders is true
      let filesToProcess = acceptedFiles

      if (!allowFolders) {
        const filesOnly = acceptedFiles.filter(file => {
          // Check if it's a folder by looking at the webkitRelativePath
          // If webkitRelativePath exists and has path separators, it's likely a folder
          return !file.webkitRelativePath || file.webkitRelativePath.split('/').length === 1
        })

        if (filesOnly.length === 0) {
          // All dropped items were folders, show a message
          toast.error('Folder uploads are not allowed. Please select files only.')

          return
        }

        if (filesOnly.length < acceptedFiles.length) {
          // Some items were filtered out, show a message
          toast.info(
            `${acceptedFiles.length - filesOnly.length} folder(s) were ignored. Only files can be uploaded.`,
          )
        }

        filesToProcess = filesOnly
      } else {
        // Process folders and preserve folder structure
        filesToProcess = acceptedFiles.map(file => {
          // Check if it's a folder by looking at the webkitRelativePath
          const isFolder = file.webkitRelativePath && file.webkitRelativePath.split('/').length > 1

          if (isFolder) {
            // Extract the relative path from the file's webkitRelativePath
            const relativePath = file.webkitRelativePath
            const pathParts = relativePath.split('/')

            pathParts.pop() // Get the filename
            const folderPath = pathParts.join('/') // Get the folder path

            return {
              ...file,
              path: folderPath || undefined, // Set the path for folder structure preservation
            }
          }

          return file
        })
      }

      const newFiles = filesToProcess.map((file, index) =>
        Object.assign(file, {
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
          uploadProgress: 0,
          status: 'uploading' as const,
          id: index + 1,
        }),
      )

      onFilesAccepted(newFiles)
    },
    [onFilesAccepted, allowFolders],
  )

  const { getRootProps, getInputProps, isDragReject, fileRejections } = useDropzone({
    onDrop,
    maxSize,
    maxFiles,
    accept: acceptedFileTypes,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
    noClick: false,
    noKeyboard: false,
  })

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center
          transition-all duration-200 ease-in-out
          ${isDragActive ? 'border-blue-500 bg-blue-50/5' : 'border-gray-300'}
          ${isDragReject ? 'border-red-500 bg-red-50/5' : ''}
        `}
      >
        <input {...getInputProps()} />
        <AnimatePresence>
          {isDragActive ? (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="absolute inset-0 flex items-center justify-center bg-blue-500/10 rounded-lg"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              <p className="text-blue-500 font-medium">Drop files here...</p>
            </motion.div>
          ) : (
            <div>
              <p className="text-gray-300">Drag & drop files here, or click to select files</p>
              {allowFolders && (
                <p className="text-gray-400 text-sm mt-2">Folders are also supported</p>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>

      {fileRejections.length > 0 && (
        <div className="mt-4 text-red-500 text-sm">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name}>
              {file.name} - {errors.map(e => e.message).join(', ')}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
